<template>
  <ig-page-base>
    <div class="box invoices__content">
      <div class="product-import-and-export__title-box">
        <h2 class="title product-import-and-export__title has-text-weight-semibold">
          {{ $t('storePages.invoices.title') }}
        </h2>
      </div>

      <div class="invoices__filters">
        <div class="columns">
          <div class="column is-4">
            <b-datepicker
              v-model="dates"
              :placeholder="$t('storePages.invoices.filters.rangeDate')"
              :disabled="loading"
              locale="pt-BR"
              icon="calendar"
              @range-end="changeDates"
              trap-focus
              range>
              <b-button
                label="Limpar"
                type="is-danger"
                icon-left="close"
                outlined
                @click="clearDate" />
            </b-datepicker>
          </div>
          <div class="column is-6">
            <form @submit.prevent="getInvoiceList()">
              <b-field grouped>
                <b-input
                  v-model="filters.query"
                  :placeholder="$t('storePages.invoices.filters.query')"
                  expanded />
                <p class="control">
                  <b-button
                    native-type="submit"
                    type="is-primary"
                    :label="$t('storePages.invoices.filters.btnSearch')"
                    :loading="loading"
                    :disabled="loading"
                  ></b-button>
                </p>
              </b-field>
            </form>
          </div>
          <div class="column has-text-right">
            <b-button
              type="is-primary"
              icon-left="filter"
              outlined
              :label="$t('storePages.invoices.filters.btnFilters')"
              :disabled="loading"
              @click="showFilters = true"
            ></b-button>

            <ig-sidebar-filters
              v-model="showFilters"
              v-slot="{ filters }"
              @submit="applyFilters"
            >
              <b-field
                :label="$t('storePages.invoices.filters.status.label')"
                label-position="inside">
                <b-select
                  v-model="filters.status"
                  :placeholder="$t('storePages.invoices.filters.status.placeholder')"
                  expanded>
                  <option :value="status" v-for="status in listOfStatus" :key="status">
                    {{
                      $t(`storePages.invoices.filters.status.options.${status}`)
                    }}
                  </option>
                </b-select>
              </b-field>
            </ig-sidebar-filters>
          </div>
        </div>
      </div>

      <hr />

      <div class="has-text-right">
        <b-dropdown
          position="is-bottom-left"
          append-to-body
          aria-role="menu"
          trap-focus
          :disabled="total === 0 || loading">
          <template #trigger="{ active }">
            <b-button
              :label="$t('storePages.invoices.table.actionsBatch.title')"
              type="is-warning"
              size="is-small"
              :icon-right="active ? 'chevron-up' : 'chevron-down'"
            />
          </template>

          <b-dropdown-item aria-role="listitem">
            <b-icon icon="download" />
            {{ $t('storePages.invoices.table.actionsBatch.allXml', { length: total }) }}
          </b-dropdown-item>

          <b-dropdown-item aria-role="listitem" :disabled="checkedRows.length === 0">
            <b-icon icon="download" />
            {{ $t(
            'storePages.invoices.table.actionsBatch.selectedXml', { length: checkedRows.length })
            }}
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <b-table
        :loading="loading"
        :data="invoices"
        backend-sorting
        default-sort="createdAt"
        default-sort-direction="desc"
        paginated
        pagination-simple
        backend-pagination
        @page-change="onPageChange"
        :total="total"
        :per-page="filters.limit"
        :current-page.sync="filters.page"
        @sort="sort"
        striped
        hoverable
        scrollable
        checkable
        :checked-rows.sync="checkedRows"
      >
        <template #empty>
          <ig-empty />
        </template>

        <template #bottom-left>
          <ig-per-page
            :total-displayed="invoices.length"
            :total="total"
            :perPage="filters.limit"
            @change="changePerPage"
          />
        </template>

        <b-table-column
          field="number"
          :label="$t('storePages.invoices.table.number')"
          width="120"
          sortable
        >
          <template v-slot="props">
            <span>{{ String(props.row.number).padStart(6, '0') }} - {{ props.row.series }}</span>
          </template>
        </b-table-column>

        <b-table-column
          field="createdAt"
          :label="$t('storePages.invoices.table.emissionDate')"
          width="130"
          centered
          sortable
        >
          <template v-slot="props">
            {{ getFormattedDate(props.row.createdAt) }}
          </template>
        </b-table-column>

        <b-table-column
          :label="$t('storePages.invoices.table.customer')"
          sortable
        >
          <template v-slot="props">
            {{ props.row.recipient.name || '-' }}
          </template>
        </b-table-column>

        <b-table-column
          width="20"
          :label="$t('storePages.invoices.table.customerStateCode')"
          sortable
        >
          <template v-slot="props">
            {{ props.row.recipient.address.stateCode || '-' }}
          </template>
        </b-table-column>

        <b-table-column
          width="50"
          :label="$t('storePages.invoices.table.status')"
          centered
          sortable
        >
          <template v-slot="props">
            <b-tag class="is-uppercase is-light" :type="getStatusInfo(props.row.status).type">
              {{ getStatusInfo(props.row.status).label }}
            </b-tag>
            <br>
            <p
              style="font-size: 0.6rem;margin: 0.5rem 0;"
              v-if="props.row.environment === 'SANDBOX'">
              <code>ambiente:</code> {{ $t('storePages.invoices.table.environmentSandbox') }}
            </p>
          </template>
        </b-table-column>

        <b-table-column
          width="80"
          :label="$t('storePages.invoices.table.price')"
        >
          <template v-slot="props">
            {{ getFormattedPrice(props.row.total) }}
          </template>
        </b-table-column>

        <b-table-column
          :label="$t('storePages.invoices.table.orderNumber')"
        >
          <template v-slot="props">
            <router-link
              v-if="props.row.order"
              :to="{ name: 'orderDetail', params: { id: props.row.order } }">
              #{{ props.row.orderNumber }}
            </router-link>
            <span v-else>
              #{{ props.row.orderNumber }}
            </span>
          </template>
        </b-table-column>

        <b-table-column width="80">
          <template v-slot="props">
            <b-dropdown
              position="is-bottom-left"
              append-to-body
              aria-role="menu"
              trap-focus>
              <template #trigger="{ active }">
                <b-button
                  :label="$t('storePages.invoices.table.actions.title')"
                  type="is-primary"
                  size="is-small"
                  :icon-left="props.row.errorMessage ? 'exclamation' : ''"
                  :icon-right="active ? 'chevron-up' : 'chevron-down'"
                  outlined
                />
              </template>

              <b-dropdown-item
                aria-role="listitem"
                v-if="props.row.errorMessage"
                @click="showInvoiceError(props.row.errorMessage)"
              >
                <b-icon icon="exclamation-triangle" />
                {{ $t('storePages.invoices.table.actions.viewErrors') }}
              </b-dropdown-item>

              <b-dropdown-item
                has-link
                aria-role="listitem"
                :disabled="!props.row.linkXml"
              >
                <a
                  :href="props.row.linkXml"
                  :class="{'is-disabled': !props.row.linkXml}"
                  target="_blank"
                >
                  <b-icon icon="download" />
                  {{ $t('storePages.invoices.table.actions.xml') }}
                </a>
              </b-dropdown-item>

              <b-dropdown-item
                has-link
                aria-role="listitem"
                :disabled="!props.row.linkDanfe"
              >
                <a
                  :href="props.row.linkDanfe"
                  :class="{'is-disabled': !props.row.linkDanfe}"
                  target="_blank"
                >
                  <b-icon icon="file-pdf" />
                  {{ $t('storePages.invoices.table.actions.pdf') }}
                </a>
              </b-dropdown-item>

              <b-dropdown-item
                has-link
                aria-role="listitem"
                :disabled="!props.row.linkDanfeSimple"
              >
                <a
                  :href="props.row.linkDanfeSimple"
                  :class="{'is-disabled': !props.row.linkDanfeSimple}"
                  target="_blank"
                >
                  <b-icon icon="file-pdf" />
                  {{ $t('storePages.invoices.table.actions.pdfSimple') }}
                </a>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table-column>
      </b-table>
    </div>
  </ig-page-base>
</template>

<script>
import Vue from 'vue';
import { createNamespacedHelpers } from 'vuex';
import * as Moment from 'moment';

import {
  IgPageBase,
  IgEmpty,
  IgSidebarFilters,
  IgPerPage,
} from '@integrai/ui';

const { mapState, mapActions } = createNamespacedHelpers('invoices');

export default Vue.extend({
  name: 'page-invoices',
  components: {
    IgPageBase,
    IgEmpty,
    IgSidebarFilters,
    IgPerPage,
  },
  data() {
    return {
      showFilters: false,
      dates: null,
      checkedRows: [],
      filters: {
        limit: 10,
        page: 1,
        sortField: 'createdAt',
        sortOrder: 'desc',
        startCreateDate: null,
        endCreateDate: null,
      },
      listOfStatus: [
        'PENDING',
        'AUTHORIZED',
        'REJECTED',
        'CANCELED',
      ],
    };
  },
  computed: {
    ...mapState([
      'loading',
      'invoices',
      'total',
    ]),
  },
  methods: {
    ...mapActions([
      'getInvoices',
    ]),
    getFormattedPrice(price) {
      if (price !== undefined) {
        return price.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          style: 'currency',
          currency: 'BRL',
        });
      }

      return '-';
    },
    getPaymentMethodLabel(method) {
      const labels = {
        BOLETO: 'Boleto',
        CREDIT_CARD: 'Cartão de Cŕedito',
        PIX: 'Pix',
      };

      return labels[method] || '-';
    },
    getStatusInfo(status) {
      return {
        PENDING: {
          label: 'Pendente',
          type: 'is-warning',
        },
        AUTHORIZED: {
          label: 'Autorizada',
          type: 'is-success',
        },
        REJECTED: {
          label: 'Rejeitada',
          type: 'is-danger',
        },
        CANCELED: {
          label: 'Cancelada',
          type: 'is-danger',
        },
      }[status];
    },
    getFulfillmentStatus(status) {
      return {
        WAITING_STOCK: 'is-info',
        PROCESSING: 'is-warning',
        UNPACKED: 'is-primary',
        PACKED: 'is-info',
        DISPATCHED: 'is-success',
        IN_TRANSIT: 'is-info',
        DELIVERED: 'is-success',
        CANCELED: 'is-danger',
      }[status] || 'is-default';
    },
    getFormattedDate(date) {
      return Moment(date).format('DD/MM/YYYY HH:mm:ss');
    },
    changeDates() {
      this.$nextTick(() => {
        const [
          startCreateDate,
          endCreateDate,
        ] = this.dates || [];

        Object.assign(this.filters, {
          startCreateDate,
          endCreateDate: endCreateDate
            ? Moment(endCreateDate).endOf('day').toISOString() : undefined,
        });
      });
    },
    clearDate() {
      this.dates = null;
      this.filters.startCreateDate = null;
      this.filters.endCreateDate = null;
    },
    sort(sortField, sortOrder) {
      Object.assign(this.filters, {
        sortField,
        sortOrder,
      });

      this.getInvoiceList();
    },
    onPageChange(page) {
      Object.assign(this.filters, {
        page,
      });

      this.getInvoiceList();
    },
    changePerPage(limit) {
      Object.assign(this.filters, {
        limit,
      });

      this.getInvoiceList();
    },
    applyFilters(filters) {
      this.getInvoiceList({
        ...this.filters,
        ...filters || {},
      });
    },
    async getInvoiceList(filters) {
      await this.getInvoices(filters || this.filters);
    },
    showInvoiceError(errorMessage) {
      this.$buefy.dialog.alert(errorMessage);
    },
  },
  async beforeMount() {
    await this.getInvoiceList();
  },
});
</script>

<style lang="scss">
  .invoices {
    &__title-box{
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }
    &__title{
      padding-bottom: 1rem;
    }
    &__line-through {
      text-decoration: line-through;
    }
  }
</style>
